import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ShieldCheck,
    Calendar,
    Mail,
    Download,
    Loader2,
    Clock,
    CheckCircle2,
    AlertTriangle,
    Send,
    Plus,
    X,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";

/**
 * Admin → Compliance Settings page.
 *
 * Single-screen control surface for the quarterly compliance digest:
 *   - Live preview of the upcoming quarter's KPIs
 *   - Last-sent / next-scheduled status banner
 *   - Recipient toggle list (default = all admins, override w/ custom emails)
 *   - Dry-run + real send buttons (with explicit confirm)
 *   - Standalone .ics download for the audit calendar
 */
export default function AdminCompliancePage() {
    const { t } = useTranslation();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [status, setStatus] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    const [year, setYear] = useState("");
    const [quarter, setQuarter] = useState("");
    const [selectedRecipients, setSelectedRecipients] = useState(new Set());
    const [extraEmail, setExtraEmail] = useState("");
    const [extraEmails, setExtraEmails] = useState([]);

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") {
            navigate("/");
        }
    }, [user, authLoading, navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const params = year && quarter ? { year, quarter } : {};
            const [s, p] = await Promise.all([
                api.get("/admin/compliance/status"),
                api.get("/admin/compliance/quarterly-digest/preview", { params }),
            ]);
            setStatus(s.data);
            setPreview(p.data);
            setSelectedRecipients(new Set((s.data.admin_recipients || []).map((a) => a.email)));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_compliance.toast_load_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, year, quarter]);

    const allRecipientEmails = useMemo(() => {
        return [...selectedRecipients, ...extraEmails];
    }, [selectedRecipients, extraEmails]);

    const send = async (dryRun) => {
        if (allRecipientEmails.length === 0) {
            toast.error(t("admin_compliance.toast_no_recipients"));
            return;
        }
        if (!dryRun && !window.confirm(t("admin_compliance.confirm_send", { count: allRecipientEmails.length }))) {
            return;
        }
        setSending(true);
        try {
            const body = {
                dry_run: dryRun,
                recipients: allRecipientEmails,
            };
            if (year && quarter) {
                body.year = Number(year);
                body.quarter = Number(quarter);
            }
            const { data } = await api.post("/admin/compliance/quarterly-digest", body);
            if (dryRun) {
                toast.success(t("admin_compliance.toast_dryrun_ok", { count: allRecipientEmails.length }));
            } else {
                toast.success(t("admin_compliance.toast_sent_ok", { sent: data.sent, ics: data.ics_filename }));
                load();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_compliance.toast_send_failed"));
        } finally {
            setSending(false);
        }
    };

    const downloadIcs = async () => {
        try {
            const params = year && quarter ? { year, quarter } : {};
            const r = await api.get("/admin/compliance/quarterly-digest/calendar.ics", {
                params, responseType: "blob",
            });
            const cd = r.headers["content-disposition"] || "";
            const m = /filename="([^"]+)"/.exec(cd);
            const filename = m ? m[1] : "twowheels-compliance.ics";
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(t("admin_compliance.toast_ics_downloaded"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("admin_compliance.toast_ics_failed"));
        }
    };

    const toggleRecipient = (email) => {
        const next = new Set(selectedRecipients);
        next.has(email) ? next.delete(email) : next.add(email);
        setSelectedRecipients(next);
    };

    const addExtraEmail = () => {
        const v = extraEmail.trim().toLowerCase();
        if (!v || !v.includes("@") || extraEmails.includes(v)) return;
        setExtraEmails([...extraEmails, v]);
        setExtraEmail("");
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00]" />
            </div>
        );
    }

    const lastSent = status?.last_sent;
    const window_ = status?.next_due_window;
    const yearOpts = [2026, 2025, 2024];

    return (
        <div className="min-h-screen bg-white" data-testid="admin-compliance-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {/* Header */}
                <div className="mb-8">
                    <Link to="/admin/dealers" className="text-xs font-bold uppercase tracking-wider text-[#6B7280] hover:text-[#111827]" data-testid="back-to-dealers">
                        ← {t("admin_compliance.back_to_dealers")}
                    </Link>
                    <div className="flex items-center gap-3 mt-3">
                        <ShieldCheck className="w-7 h-7 text-[#FFCC00]" />
                        <h1 className="text-4xl sm:text-5xl font-black text-[#111827] font-display tracking-tight">
                            {t("admin_compliance.title")}
                        </h1>
                    </div>
                    <p className="text-sm text-[#6B7280] mt-2 max-w-3xl">
                        {t("admin_compliance.subtitle")}
                    </p>
                    <Link
                        to="/admin/compliance/history"
                        className="inline-flex items-center gap-2 mt-3 text-xs font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1D4ED8]"
                        data-testid="link-compliance-history"
                    >
                        <Clock className="w-3.5 h-3.5" /> Versand-Verlauf öffnen
                    </Link>
                    <Link
                        to="/admin/compliance/spam"
                        className="inline-flex items-center gap-2 mt-3 ml-4 text-xs font-bold uppercase tracking-wider text-red-700 hover:text-red-900"
                        data-testid="link-spam-flags"
                    >
                        ⚠ Spam-Verdacht prüfen
                    </Link>
                    <Link
                        to="/admin/doku"
                        className="inline-flex items-center gap-2 mt-3 ml-4 text-xs font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1D4ED8]"
                        data-testid="link-admin-doku"
                    >
                        📋 Datenschutz-Doku &amp; Consent-Log
                    </Link>
                </div>

                {/* Status banner */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="compliance-status-cards">
                    <StatusCard
                        icon={<Clock className="w-4 h-4" />}
                        label={t("admin_compliance.card_last_sent")}
                        value={lastSent ? `Q${lastSent.quarter} ${lastSent.year}` : "—"}
                        sub={lastSent
                            ? `${(lastSent.at || "").slice(0, 10)} · ${lastSent.sent}/${lastSent.recipients} · ${t(`admin_compliance.trigger_${lastSent.trigger}`)}`
                            : t("admin_compliance.no_sends_yet")}
                        tone="gray"
                        testid="card-last-sent"
                    />
                    <StatusCard
                        icon={<Calendar className="w-4 h-4" />}
                        label={t("admin_compliance.card_next_scheduled")}
                        value={status?.next_scheduled?.label || "—"}
                        sub={window_
                            ? `${t("admin_compliance.window_label")}: ${(window_.start || "").slice(0, 10)} → ${(window_.end || "").slice(0, 10)}`
                            : ""}
                        tone="amber"
                        testid="card-next-scheduled"
                    />
                    <StatusCard
                        icon={window_?.already_sent ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                        label={t("admin_compliance.card_window_status")}
                        value={window_?.already_sent ? t("admin_compliance.sent_for_period") : t("admin_compliance.pending_for_period")}
                        sub={`${t("admin_compliance.interval")}: ${Math.round((status?.digest_interval_seconds || 0) / 3600)}h`}
                        tone={window_?.already_sent ? "green" : "amber"}
                        testid="card-window-status"
                    />
                </div>

                {/* Period selector + Preview */}
                <Card title={t("admin_compliance.section_preview")} testid="section-preview">
                    <div className="flex flex-wrap items-end gap-3 mb-6">
                        <SelectField
                            label={t("admin_compliance.label_year")}
                            value={year || ""}
                            onChange={setYear}
                            options={[{ value: "", label: t("admin_compliance.default_period") }, ...yearOpts.map((y) => ({ value: String(y), label: String(y) }))]}
                            testid="select-year"
                        />
                        <SelectField
                            label={t("admin_compliance.label_quarter")}
                            value={quarter || ""}
                            onChange={setQuarter}
                            options={[
                                { value: "", label: "—" },
                                { value: "1", label: "Q1" },
                                { value: "2", label: "Q2" },
                                { value: "3", label: "Q3" },
                                { value: "4", label: "Q4" },
                            ]}
                            disabled={!year}
                            testid="select-quarter"
                        />
                        <div className="ml-auto">
                            <Badge className="bg-[#FFCC00] text-[#111827] hover:bg-[#FFCC00] font-bold uppercase tracking-wider" data-testid="active-period-badge">
                                {preview?.label || "—"}
                            </Badge>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="preview-kpis">
                        <KpiCell label={t("admin_compliance.kpi_new_dealers")} value={preview?.new_dealers} />
                        <KpiCell label={t("admin_compliance.kpi_requests")} value={preview?.requests} />
                        <KpiCell label={t("admin_compliance.kpi_approvals")} value={preview?.approvals} tone="green" />
                        <KpiCell label={t("admin_compliance.kpi_rejections")} value={preview?.rejections} tone="red" />
                        <KpiCell label={t("admin_compliance.kpi_resubmissions")} value={preview?.resubmissions} />
                        <KpiCell label={t("admin_compliance.kpi_pending_now")} value={preview?.pending_now} tone="amber" />
                    </div>

                    {(preview?.rejection_notes || []).length > 0 && (
                        <div className="mt-6 border-l-4 border-red-500 bg-red-50 p-4 rounded-r-sm" data-testid="rejection-notes">
                            <div className="text-xs font-bold uppercase tracking-wider text-red-900 mb-2">
                                {t("admin_compliance.recent_rejections")}
                            </div>
                            <ul className="space-y-2">
                                {preview.rejection_notes.map((n, i) => (
                                    <li key={i} className="text-xs text-[#111827]">
                                        <span className="font-bold">{n.email}</span>
                                        <span className="text-gray-500 ml-2">{n.at}</span>
                                        <div className="italic text-gray-700 mt-0.5">„{n.note}"</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card>

                {/* Recipients */}
                <Card title={t("admin_compliance.section_recipients")} testid="section-recipients">
                    <p className="text-xs text-gray-500 mb-4">{t("admin_compliance.recipients_hint")}</p>

                    <div className="space-y-2 mb-4" data-testid="recipients-list">
                        {(status?.admin_recipients || []).map((a) => (
                            <label
                                key={a.id}
                                className="flex items-center gap-3 p-2 hover:bg-[#FFFBEB] rounded-sm cursor-pointer transition-colors"
                                data-testid={`recipient-${a.email}`}
                            >
                                <Checkbox
                                    checked={selectedRecipients.has(a.email)}
                                    onCheckedChange={() => toggleRecipient(a.email)}
                                    data-testid={`recipient-toggle-${a.email}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-[#111827] truncate">{a.name || "—"}</div>
                                    <div className="text-xs text-gray-500 truncate">{a.email}</div>
                                </div>
                                <Badge variant="outline" className="text-[10px] font-bold uppercase">admin</Badge>
                            </label>
                        ))}
                        {extraEmails.map((em) => (
                            <div
                                key={em}
                                className="flex items-center gap-3 p-2 bg-blue-50 rounded-sm"
                                data-testid={`extra-recipient-${em}`}
                            >
                                <Checkbox checked disabled />
                                <div className="flex-1 text-sm text-[#1E3A8A] truncate">{em}</div>
                                <Badge variant="outline" className="text-[10px] font-bold uppercase bg-blue-100">extra</Badge>
                                <button
                                    type="button"
                                    onClick={() => setExtraEmails(extraEmails.filter((x) => x !== em))}
                                    className="text-blue-700 hover:text-blue-900"
                                    data-testid={`remove-extra-${em}`}
                                    aria-label={t("admin_compliance.remove_extra")}
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2">
                        <Input
                            type="email"
                            value={extraEmail}
                            onChange={(e) => setExtraEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtraEmail(); } }}
                            placeholder={t("admin_compliance.extra_email_placeholder")}
                            className="rounded-sm border-2"
                            data-testid="extra-email-input"
                        />
                        <Button
                            type="button"
                            onClick={addExtraEmail}
                            variant="outline"
                            className="rounded-sm border-2 border-[#111827] font-bold uppercase text-xs tracking-wider"
                            data-testid="extra-email-add"
                        >
                            <Plus className="w-4 h-4 mr-1" /> {t("admin_compliance.add")}
                        </Button>
                    </div>

                    <div className="mt-3 text-xs text-gray-500" data-testid="recipients-count">
                        {t("admin_compliance.total_recipients", { count: allRecipientEmails.length })}
                    </div>
                </Card>

                {/* Actions */}
                <div className="bg-[#111827] text-white rounded-sm p-6 mt-6" data-testid="compliance-actions">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="flex-1">
                            <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-1">
                                {t("admin_compliance.actions_header")}
                            </div>
                            <div className="text-sm text-white/80">
                                {t("admin_compliance.actions_subtitle", { count: allRecipientEmails.length })}
                            </div>
                        </div>
                        <Button
                            type="button"
                            onClick={downloadIcs}
                            variant="outline"
                            className="rounded-sm border-white/30 text-white hover:bg-white/10 bg-transparent font-bold text-xs uppercase tracking-wider h-11"
                            data-testid="download-ics-btn"
                        >
                            <Download className="w-4 h-4 mr-2" /> {t("admin_compliance.btn_download_ics")}
                        </Button>
                        <Button
                            type="button"
                            onClick={() => send(true)}
                            disabled={sending || allRecipientEmails.length === 0}
                            variant="outline"
                            className="rounded-sm border-white/30 text-white hover:bg-white/10 bg-transparent font-bold text-xs uppercase tracking-wider h-11"
                            data-testid="dry-run-btn"
                        >
                            {sending
                                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                : <Mail className="w-4 h-4 mr-2" />}
                            {t("admin_compliance.btn_dry_run")}
                        </Button>
                        <Button
                            type="button"
                            onClick={() => send(false)}
                            disabled={sending || allRecipientEmails.length === 0}
                            className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] rounded-sm font-bold text-xs uppercase tracking-wider h-11"
                            data-testid="send-now-btn"
                        >
                            {sending
                                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                : <Send className="w-4 h-4 mr-2" />}
                            {t("admin_compliance.btn_send_now")}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---- Inline UI helpers (kept local — not reused elsewhere) ----

function StatusCard({ icon, label, value, sub, tone, testid }) {
    const tones = {
        gray: "bg-gray-50 border-gray-200 text-[#111827]",
        amber: "bg-amber-50 border-amber-300 text-[#92400E]",
        green: "bg-green-50 border-green-300 text-[#065F46]",
    };
    return (
        <div className={`border-l-4 rounded-r-sm p-4 ${tones[tone] || tones.gray}`} data-testid={testid}>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-1.5">
                {icon}{label}
            </div>
            <div className="text-2xl font-black font-display tracking-tight">{value}</div>
            {sub && <div className="text-xs mt-1">{sub}</div>}
        </div>
    );
}

function KpiCell({ label, value, tone }) {
    const tones = {
        green: "text-green-700",
        red: "text-red-700",
        amber: "text-amber-700",
        default: "text-[#111827]",
    };
    return (
        <div className="border border-gray-200 rounded-sm p-3 text-center">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</div>
            <div className={`text-2xl font-black font-mono-num ${tones[tone] || tones.default}`}>
                {value ?? "—"}
            </div>
        </div>
    );
}

function Card({ title, children, testid }) {
    return (
        <section className="border-2 border-[#111827] rounded-sm bg-white p-5 sm:p-6 mb-6" data-testid={testid}>
            <h2 className="text-lg font-black uppercase tracking-wider mb-4 text-[#111827]">{title}</h2>
            {children}
        </section>
    );
}

function SelectField({ label, value, onChange, options, disabled, testid }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="border-2 border-[#111827] rounded-sm px-3 py-2 text-sm font-bold bg-white disabled:opacity-50"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}
